"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Pencil, Trash2, Briefcase, Loader2 } from "lucide-react";
import { fetchLeaveTypes, createLeaveType, updateLeaveType, deleteLeaveType } from "../../../services/leaveTypeService";
import { toast } from "react-hot-toast";

const DURATIONS = [
  { value: "full-day", label: "Full Day" },
  { value: "half-day", label: "Half Day" },
  { value: "short-leave", label: "Short Leave" },
];

const PAY_TYPES = [
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

const defaultForm = {
  name: "",
  code: "",
  duration: "full-day",
  payType: "unpaid",
  deductFromSalary: true,
  deductionFactor: 1,
  maxDaysPerYear: "",
  description: "",
};

export default function LeaveTypesManagement() {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchLeaveTypes(false);
      if (res.success) setLeaveTypes(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load leave types");
      setLeaveTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name,
      code: row.code,
      duration: row.duration || "full-day",
      payType: row.payType || "unpaid",
      deductFromSalary: row.deductFromSalary !== false,
      deductionFactor: row.deductionFactor ?? 1,
      maxDaysPerYear: row.maxDaysPerYear ?? "",
      description: row.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name?.trim() || !form.code?.trim()) {
      toast.error("Name and code are required");
      return;
    }
    setSubmitLoading(true);
    setMessage({ type: "", content: "" });
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        duration: form.duration,
        payType: form.payType,
        deductFromSalary: form.payType === "paid" ? false : form.deductFromSalary,
        deductionFactor: form.deductionFactor,
        maxDaysPerYear: form.maxDaysPerYear ? Number(form.maxDaysPerYear) : null,
        description: form.description?.trim() || "",
      };
      if (editingId) {
        await updateLeaveType(editingId, payload);
        toast.success("Leave type updated");
      } else {
        await createLeaveType(payload);
        toast.success("Leave type created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setMessage({
        type: "error",
        content: e.response?.data?.message || "Failed to save",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const [toDelete, setToDelete] = useState(null);
  const handleDelete = async () => {
    if (!toDelete) return;
    setSubmitLoading(true);
    try {
      await deleteLeaveType(toDelete._id);
      toast.success("Leave type deleted");
      setDeleteDialogOpen(false);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
              <Briefcase className="h-5 w-5 text-[#0263cc]" />
              Leave Types
            </CardTitle>
            <Button
              onClick={openCreate}
              className="bg-[#0263cc] hover:bg-[#0255b0] text-white shrink-0"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Leave Type
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Manage leave types (full day, half day, short leave, paid/unpaid) for salary calculation.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Duration</TableHead>
                  <TableHead className="font-semibold">Pay Type</TableHead>
                  <TableHead className="font-semibold">Deduct</TableHead>
                  <TableHead className="font-semibold">Factor</TableHead>
                  <TableHead className="font-semibold">Max Days/Year</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaveTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-slate-500">
                      No leave types yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveTypes.map((row) => (
                    <TableRow key={row._id} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {row.duration?.replace("-", " ") || "Full day"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={row.payType === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                          {row.payType}
                        </Badge>
                      </TableCell>
                      <TableCell>{row.deductFromSalary !== false ? "Yes" : "No"}</TableCell>
                      <TableCell>{row.deductionFactor ?? 1}</TableCell>
                      <TableCell>{row.maxDaysPerYear ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(row)} className="text-slate-600">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { setToDelete(row); setDeleteDialogOpen(true); }} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">{editingId ? "Edit Leave Type" : "Add Leave Type"}</DialogTitle>
            <DialogDescription>Configure name, code, duration and salary impact.</DialogDescription>
          </DialogHeader>
          {message.content && (
            <Alert variant={message.type === "error" ? "destructive" : "default"}>
              <AlertDescription>{message.content}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Casual Leave"
                />
              </div>
              <div className="space-y-2">
                <Label>Code <span className="text-red-500">*</span></Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. CL"
                  disabled={!!editingId}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration <span className="text-red-500">*</span></Label>
                <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pay Type <span className="text-red-500">*</span></Label>
                <Select value={form.payType} onValueChange={(v) => setForm((f) => ({ ...f, payType: v, deductFromSalary: v === "paid" ? false : f.deductFromSalary }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAY_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.payType === "unpaid" && (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="deduct"
                    checked={form.deductFromSalary}
                    onChange={(e) => setForm((f) => ({ ...f, deductFromSalary: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <Label htmlFor="deduct">Deduct from salary</Label>
                </div>
                <div className="space-y-2">
                  <Label>Deduction factor (0–1) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min={0}
                    max={1}
                    step={0.25}
                    value={form.deductionFactor}
                    onChange={(e) => setForm((f) => ({ ...f, deductionFactor: Number(e.target.value) || 0 }))}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Max days per year (optional)</Label>
              <Input
                type="number"
                min={0}
                value={form.maxDaysPerYear}
                onChange={(e) => setForm((f) => ({ ...f, maxDaysPerYear: e.target.value }))}
                placeholder="e.g. 12 for CL, 10 for SL — leave empty for no limit"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Short description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitLoading} className="bg-[#0263cc] hover:bg-[#0255b0] text-white">
              {submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Leave Type</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{toDelete?.name}&quot;? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitLoading}>
              {submitLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
